/**
 * @fileoverview Serialize a SpreadsheetSpec into the `spreadsheetBlock` HTML
 * element and parse it back, plus a shared static-`<table>` renderer.
 *
 * The spec lives JSON-encoded in a single attribute so the canvas `html`
 * field round-trips unchanged (same scheme as `chartHtml.ts`). The static
 * table renderer is reused by both the on-screen NodeView and the PDF export
 * (`canvasToPdfBlob`), which cannot run React and so needs real `<table>`
 * markup rather than the empty placeholder div.
 */
import {
  SpreadsheetSpecSchema,
  type SpreadsheetSpec,
} from "@cvx/lib/spreadsheets/spec";

export function encodeSpreadsheetAttr(spec: SpreadsheetSpec): string {
  return JSON.stringify(spec).replace(/'/g, "&#39;").replace(/</g, "&lt;");
}

export function spreadsheetSpecFromAttr(attr: string): SpreadsheetSpec | null {
  try {
    const json = attr.replace(/&#39;/g, "'").replace(/&lt;/g, "<");
    const parsed = SpreadsheetSpecSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Block element TipTap parses into a `spreadsheetBlock` node. */
export function specToSpreadsheetHtml(spec: SpreadsheetSpec): string {
  return `<div data-morgan-spreadsheet='${encodeSpreadsheetAttr(spec)}'></div>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cellText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return escapeHtml(String(value));
}

/**
 * Render a SpreadsheetSpec to static `<table>` markup. One table per sheet;
 * when there is more than one sheet, each is preceded by an `<h3>` of its
 * name. Used by the canvas NodeView and the PDF export.
 */
export function spreadsheetToTableHtml(spec: SpreadsheetSpec): string {
  const multi = spec.sheets.length > 1;
  return spec.sheets
    .map((sheet) => {
      const head = sheet.columns
        .map((c) => `<th>${escapeHtml(c.label)}</th>`)
        .join("");
      const body = sheet.rows
        .map(
          (row) =>
            `<tr>${sheet.columns
              .map((c) => `<td>${cellText(row[c.key])}</td>`)
              .join("")}</tr>`,
        )
        .join("");
      const title = multi ? `<h3>${escapeHtml(sheet.name)}</h3>` : "";
      return `${title}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join("");
}
